import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { GetTelemetrySettingsResponse } from 'core/api';
import { EventType } from './EventType';
import LocalStorage from './LocalStorage';

const MILLIS_PER_HOUR = 60 * 60 * 1000;
const HOURS_BETWEEN_SAMPLING_SETTINGS_UPDATE = 4;
const HOURS_BETWEEN_SENDING_STATS = 24;

// Necesito mantenerlo como nullable (y tenerlo en cuenta) porque se puede intentar usar
// la telemetría antes de inicializarla, por ejemplo, porque se navega a una página o
// se lanza una petición al servidor.
let appInsights: Nullable<ApplicationInsights> = null;

let config: {
	platform: 'Web' | 'Cordova' | 'Electron',
	application: 'Admin' | 'Kds' | 'Pos' | 'Tablet' | 'Smartphone',
	machineId: string,
} = {
	platform: 'Web',
	application: 'Admin',
	machineId: ''
};

let defaultProperties: Record<string, string> = {};
let samplingSettings: Array<{ key: string, value: number }> = [];

// Recibo la función getTelemetrySettings para evitar la dependencia circular sobre bus.
export async function start(cfg: typeof config, getTelemetrySettings: () => Promise<GetTelemetrySettingsResponse>): Promise<void> {

	await updateSettings(getTelemetrySettings);

	config = cfg;

	setInterval(() => {
		void updateSettings(getTelemetrySettings);
	}, HOURS_BETWEEN_SAMPLING_SETTINGS_UPDATE * MILLIS_PER_HOUR);

	// No chequeo cada X horas, como en el updateSettings, porque en este caso no se fuerza un
	// envío al arrancar y puede que estuviéramos mucho más cerca de tener que hacer el siguiente
	// envío.
	setInterval(() => {
		trackStatsIfNeeded();
	}, 60 * 1000);

	// Envío métricas a azure cada minuto para minimizar ancho de banda
	setInterval(() => {
		flushMetrics();
	}, 60 * 1000);
}

export function stats(): void {
	trackEvent('Client:Stats', '', {
		userAgent: navigator.userAgent ?? 'unknown',
		resolution: `${window.screen.width}x${window.screen.height}`,
		dpi: window.devicePixelRatio?.toString() ?? 'unknown'
	});
}

export function event(event: keyof EventType, { duration }: { duration: number }): void {
	trackMetric('Client:Event:ExecutionDuration', event, duration);
	trackEvent('Client:Event:Executed', event, { duration: duration.toString() });
}

// Me gustaría tipar page, pero no me quiero bajar el tipo Page a infrastructure
export function page(page: string, { duration }: { duration: number; }): void {
	trackMetric('Client:Page:LoadedDuration', page, duration);
	trackEvent('Client:Page:Loaded', page, { duration: duration.toString() });
}

// No puedo tipar commandName porque me tendría que bajar a infrastructure los command de pos y kds
export function command(commandName: string) {
	trackMetric('Client:Command', commandName, 1);
	trackEvent('Client:Command:Executed', commandName);
}

export function screen(title: string) {
	trackMetric('Client:Screen', title, 1);
	trackEvent('Client:Screen:Shown', title);
}

export function request(clrType: string, { duration }: { duration: number; }) {
	const request = clrType.split('.').last() ?? '';
	trackMetric('Client:Request:ExecutionDuration', request, duration);
	trackEvent('Client:Request:Executed', request, { duration: duration.toString() });
}

function trackEvent(eventName: string, tag: string, properties: Record<string, string> = {}) {
	if (shouldTrack(eventName, tag))
		appInsights?.trackEvent({ name: eventName }, addCommonProperties(tag, properties));
}

function addCommonProperties(tag: string, properties: Record<string, string>): Record<string, string> {

	const clientProperties = {
		'Version': AGORA_VERSION,
		'Application': config.application,
		'Platform': config.platform,
		'MachineId': config.machineId,
	};

	const tagProperties = tag ? { 'Tag': tag } : undefined;

	return {
		...defaultProperties,
		...clientProperties,
		...tagProperties,
		...properties
	};
}

async function updateSettings(getTelemetrySettings: () => Promise<GetTelemetrySettingsResponse>): Promise<void> {
	// El servidor ya me los da ordenados para poder aplicarlos de mayor a menor especificidad.
	const { Settings, DefaultProperties, ConnectionString } = await getTelemetrySettings();

	samplingSettings = Settings.map(({ Key, Value }) => ({ key: Key, value: Value }));

	defaultProperties = {};
	DefaultProperties.forEach(({ Key, Value }) => defaultProperties[Key] = Value);

	// Si no tengo cadena de conexión, desactivo AppInsights
	if (!ConnectionString) {
		appInsights = null;
		return;
	}

	const currentConnectionString = appInsights?.config?.connectionString;
	const connectionStringHasBeenUpdated = currentConnectionString !== ConnectionString;

	if (connectionStringHasBeenUpdated) {
		appInsights = new ApplicationInsights({
			config: {
				connectionString: ConnectionString,

				// Disable all auto-collection
				disableAjaxTracking: true,
				disableFetchTracking: true,
				disableExceptionTracking: true,
				enableAutoRouteTracking: false,
				autoTrackPageVisitTime: false,
				enableUnhandledPromiseRejectionTracking: false,
			}
		});

		appInsights.loadAppInsights();
	}
}

const trackedEvents = new Set<string>();

function shouldTrack(eventName: string, tag: string) {
	const settingName = [eventName, tag].filter(Boolean).join(':').toLowerCase();

	const samplingRate = samplingSettings.find(({ key }) => settingName.startsWith(key.toLowerCase()))?.value ?? 0;

	// Si está deshabilitado, lo considero *completamente* deshabilitado. Incluso para
	// enviar el primer evento de cada tipo. Así evito enviar las métricas una vez (que
	// ahora mismo están deshabilitadas en Azure).
	if (samplingRate === 0)
		return false;

	// Aseguro que cada evento es trackeado al menos una vez. Si no, aquellos eventos que
	// se generan muy pocas veces acaban quedando demasiado infrarrepresentados por el
	// muestreo.
	//
	// Actualmente hay unos 2.5K eventos diferentes, por lo que se pueden mantener en el
	// HashSet en memoria. Si creciese mucho el número, habría que pensar una política de
	// descartes para no consumir excesiva memoria.
	if (!trackedEvents.has(settingName)) {
		trackedEvents.add(settingName);
		return true;
	}

	return Math.random() < samplingRate;
}

function trackStatsIfNeeded() {
	const KEY = '__last_stats_sent__';
	const lastTrack = Number(LocalStorage.getItem(KEY) ?? '0') || 0;
	const totalMillis = new Date().getTime() - lastTrack;
	if (totalMillis > HOURS_BETWEEN_SENDING_STATS * MILLIS_PER_HOUR) {
		stats();
		LocalStorage.setItem(KEY, new Date().getTime().toString());
	}
}


// Simulo la agregación local de métricas para reducir ancho de banda. En el caso de C#, esto se lo come
// el SDK de Application Insights, pero en Javascript me toca hacerlo a mano. La idea es agregar mensajes
// localmente y volcarlos cada minuto a azure (uso 1 minuto porque es lo mismo que usa C#)

type MetricKey = `${string}|${string}`; // Format: metricName|tagValue

type AggregatedMetric = {
	count: number;
	sum: number;
	min: number;
	max: number;
};

const metricMap = new Map<MetricKey, AggregatedMetric>();

function trackMetric(metricName: string, tagValue: string, value: number): void {

	if (!shouldTrack('Metric:' + metricName, tagValue))
		return;

	const key: MetricKey = `${metricName}|${tagValue}`;
	const current = metricMap.get(key) ?? { count: 0, sum: 0, min: value, max: value };

	current.count += 1;
	current.sum += value;
	current.min = Math.min(current.min, value);
	current.max = Math.max(current.max, value);

	metricMap.set(key, current);
}

function flushMetrics(): void {
	for (const [key, data] of metricMap.entries()) {
		const [metricName, tagValue] = key.split('|');
		const avg = data.sum / data.count;

		appInsights?.trackMetric({
			name: metricName,
			average: avg,
			sampleCount: data.count,
			min: data.min,
			max: data.max,
			properties: { Tag: tagValue }
		});
	}

	metricMap.clear();
}